import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Search, ListFilter, Plus, Pencil, UserX, Smartphone, Upload } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { userAPI, talentAPI, User, TalentKategori, DeviceStatus } from '../../services/api';
import UserDialog from './UserDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import BrukerImportDialog from './BrukerImportDialog';

const ALLE_KATEGORIER = '__alle__';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [kategorier, setKategorier] = useState<TalentKategori[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter og søk
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategori, setFilterKategori] = useState<string>('');

  // Valgte brukere
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);

  // Import
  const [importOpen, setImportOpen] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Device status for push notifications (lazy loaded)
  const [deviceStatuses, setDeviceStatuses] = useState<Map<number, DeviceStatus>>(new Map());
  const [loadingDeviceStatus, setLoadingDeviceStatus] = useState<Set<number>>(new Set());

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const kategoriData = await talentAPI.getAllKategorier();
      setKategorier(kategoriData.kategorier || []);

      const userData = await userAPI.getAll();
      setUsers(userData.users || []);
    } catch (err: any) {
      console.error('Feil ved lasting av brukere:', err);
      if (err.response?.status === 403) {
        setError(err.response?.data?.error || 'Du har ikke tilgang');
      } else {
        setError('Kunne ikke laste brukere');
      }
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refreshUserList = async () => {
    try {
      const userData = await userAPI.getAll();
      setUsers(userData.users || []);
    } catch (err: any) {
      console.error('Feil ved oppdatering av brukerliste:', err);
      setError(err.response?.data?.error || 'Kunne ikke oppdatere brukerliste');
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await userAPI.delete(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await refreshUserList();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke slette bruker');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleBulkDeleteClick = () => {
    if (selectedUsers.size === 0) return;

    // Finn de valgte brukerne
    const usersToDelete = users.filter((u) => selectedUsers.has(u.id));
    if (usersToDelete.length > 0) {
      // Bruk første bruker som representant for bulk-sletting
      setUserToDelete({
        ...usersToDelete[0],
        firstName: `${selectedUsers.size} brukere`,
        lastName: '',
      });
      setDeleteDialogOpen(true);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedUsers.size === 0) return;

    try {
      await userAPI.bulkDelete(Array.from(selectedUsers));
      setSelectedUsers(new Set());
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await refreshUserList();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke slette brukere');
      setDeleteDialogOpen(false);
    }
  };

  const toggleSelectUser = (id: number) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map((u) => u.id)));
    }
  };

  const handleOpenDialog = (userId?: number) => {
    setSelectedUserId(userId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUserId(undefined);
  };

  const handleDialogSave = async () => {
    await refreshUserList();
    // La dialogen forbli åpen slik at brukeren kan fortsette å legge til talents
  };

  const handleRowClick = (userId: number) => {
    handleOpenDialog(userId);
  };

  // Hent device status for en bruker (lazy loading)
  const fetchDeviceStatus = async (userId: number) => {
    // Hvis allerede lastet eller lastes, ikke last på nytt
    if (deviceStatuses.has(userId) || loadingDeviceStatus.has(userId)) {
      return;
    }

    try {
      setLoadingDeviceStatus((prev) => new Set(prev).add(userId));
      const response = await userAPI.getDeviceStatus(userId);
      setDeviceStatuses((prev) => {
        const newMap = new Map(prev);
        newMap.set(userId, response.deviceStatus);
        return newMap;
      });
    } catch (err: any) {
      // Ignorer feil (bruker kan være i annen organisasjon eller ikke ha tilgang)
      console.debug('Could not fetch device status for user', userId, err);
    } finally {
      setLoadingDeviceStatus((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Hent device status for alle brukere når komponenten lastes
  useEffect(() => {
    if (users.length > 0) {
      // Lazy load device status for alle brukere (kun admin/user-roller kan ha app)
      users.forEach((user) => {
        const canHaveApp = user.roles?.some((role) => role === 'admin' || role === 'user');
        if (canHaveApp && !deviceStatuses.has(user.id) && !loadingDeviceStatus.has(user.id)) {
          fetchDeviceStatus(user.id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  // Filtering
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === '' ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesKategori =
      filterKategori === '' ||
      user.talentKategoriIds?.includes(Number.parseInt(filterKategori, 10));

    return matchesSearch && matchesKategori;
  });

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-destructive text-destructive-foreground hover:bg-destructive';
      case 'user':
        return 'bg-primary text-primary-foreground hover:bg-primary';
      default:
        return '';
    }
  };

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-sm underline">
              Lukk
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* Header med "Legg til bruker" knapp */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6" data-testid="brukere-header">
        <h3 className="text-lg font-semibold">Brukere</h3>
        <div className="flex flex-wrap gap-2" data-testid="brukere-handlinger">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Importer brukere
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Legg til bruker
          </Button>
        </div>
      </div>

      {/* Søk og filter */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="relative flex-grow min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Søk etter navn eller e-post..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="min-w-[200px]">
          <Select
            value={filterKategori || ALLE_KATEGORIER}
            onValueChange={(v) => setFilterKategori(v === ALLE_KATEGORIER ? '' : v)}
          >
            <SelectTrigger>
              <ListFilter className="mr-2 h-4 w-4 text-muted-foreground" />
              <SelectValue placeholder="Filtrer på kategori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALLE_KATEGORIER}>Alle kategorier</SelectItem>
              {kategorier
                .filter((k) => !k.parent_id)
                .map((k) => (
                  <SelectItem key={k.id} value={k.id.toString()}>
                    {k.navn}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedUsers.size > 0 && (
        <div className="flex gap-4 mb-4 p-3 bg-primary/10 rounded-md items-center">
          <p className="text-sm flex-grow">{selectedUsers.size} brukere valgt</p>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteClick}>
            <Trash2 className="mr-2 h-4 w-4" />
            Slett valgte
          </Button>
        </div>
      )}

      {/* Bruker-tabell */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={
                    selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length
                      ? 'indeterminate'
                      : filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length
                  }
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Navn</TableHead>
              <TableHead>E-post</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Roller</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>App</TableHead>
              <TableHead className="text-right">Handlinger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} className="cursor-pointer">
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedUsers.has(user.id)}
                    onCheckedChange={() => toggleSelectUser(user.id)}
                  />
                </TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium">
                      {user.firstName} {user.lastName}
                    </span>
                    {!user.isActive && (
                      <UserX className="h-4 w-4 text-status-waiting" aria-label="Inaktiv bruker" />
                    )}
                  </div>
                </TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>{user.email}</TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>{user.phoneNumber || '-'}</TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>
                  <div className="flex gap-1 flex-wrap">
                    {user.roles?.map((role) => (
                      <Badge key={role} className={getRoleBadgeClass(role)}>
                        {role}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>
                  <Badge
                    className={
                      user.isActive
                        ? 'bg-status-confirmed text-white hover:bg-status-confirmed'
                        : 'bg-status-waiting text-white hover:bg-status-waiting'
                    }
                  >
                    {user.isActive ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </TableCell>
                <TableCell onClick={() => handleRowClick(user.id)}>
                  {(() => {
                    const deviceStatus = deviceStatuses.get(user.id);
                    const canHaveApp = user.roles?.some((role) => role === 'admin' || role === 'user');

                    if (!canHaveApp) {
                      return <span className="text-xs text-muted-foreground">-</span>;
                    }

                    if (loadingDeviceStatus.has(user.id)) {
                      return <span className="text-xs text-muted-foreground">...</span>;
                    }

                    if (deviceStatus?.hasActiveDevices) {
                      return (
                        <div className="flex items-center gap-1">
                          <Smartphone
                            className="h-4 w-4 text-status-confirmed"
                            aria-label={`App knyttet (${deviceStatus.activeDeviceCount} enhet${deviceStatus.activeDeviceCount > 1 ? 'er' : ''})`}
                          />
                          <span className="text-xs text-muted-foreground">
                            {deviceStatus.activeDeviceCount}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <Smartphone
                        className="h-4 w-4 text-muted-foreground/40"
                        aria-label="Ingen app knyttet"
                      />
                    );
                  })()}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 mr-1"
                    onClick={() => handleOpenDialog(user.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteClick(user)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {filteredUsers.length === 0 && (
        <Alert className="mt-4">
          <AlertDescription>
            {searchTerm || filterKategori ? 'Ingen brukere matcher søket' : 'Ingen brukere funnet'}
          </AlertDescription>
        </Alert>
      )}

      {/* User Dialog */}
      <UserDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        userId={selectedUserId}
        onSave={handleDialogSave}
      />

      <BrukerImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportert={refreshUserList}
      />

      {/* Delete Confirmation Dialog */}
      {userToDelete && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={selectedUsers.size > 0 ? handleBulkDeleteConfirm : handleDeleteConfirm}
          title="Slett bruker"
          message="Er du sikker på at du vil slette"
          itemName={userToDelete.firstName + (userToDelete.lastName ? ' ' + userToDelete.lastName : '')}
        />
      )}
    </div>
  );
};

export default UserManagement;
